import { Stack } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { FiRepeat } from "react-icons/fi";
import { SourcingEventStatus, SourcingEventDto } from "../../autogen/bff-api";
import { MinimumRequirementsSection } from "../sourcing-events/portal/view-single/MinimumRequirementsSection";
import { SectionCard } from "../sourcing-events/portal/view-single/SectionCard";
import { AboutSection } from "./AboutSection";
import { ClassificationSection } from "./ClassificationSection";
import { ContractSection } from "./ContractSection";
import { CriteriaSection } from "./CriteriaSection";
import { DocumentsSection } from "./DocumentsSection";
import { InvitesSection } from "./InvitesSection";
import { PublicationsSection } from "./PublicationsSection";
import { ContractModificationsSection } from "./contract-modification/ContractModificationsSection";
import { ViewingsSection } from "./ViewingsSection";
import { ChangeNoticeSection } from "./changes/ChangeNoticeSection";
import { RoundSummary } from "./negotiation/RoundSummary";
import { RoundDocumentsSection } from "./rounds/RoundDocumentsSection";
import { useRoundSelection } from "./rounds/RoundSelectionContext";
import { BuyerDesignContestSection } from "./designContest/BuyerDesignContestSection";
import { isDesignContest } from "../sourcing-events/buyer/wizard/utils/procedure";

interface Props {
  event: SourcingEventDto;
}

export const RequestTab = ({ event }: Props) => {
  const { t } = useTranslation();
  const { selectedRound } = useRoundSelection();
  const { eventData } = event;
  const publicContext = event.eventContext.type === "Public" ? event.eventContext : null;
  const privateContext = event.eventContext.type === "Private" ? event.eventContext : null;
  const contest = isDesignContest(publicContext?.procedureType) ? publicContext?.designContest : null;

  // ADR-033 D2: with a round selected, the request tab IS that round's request.
  if (selectedRound) {
    return (
      <Stack spacing="5">
        <SectionCard icon={FiRepeat} title={`${t("Negotiation round")} ${selectedRound.roundNumber}`}>
          <RoundSummary round={selectedRound} eventStatus={event.status} />
        </SectionCard>
        <RoundDocumentsSection event={event} round={selectedRound} />
      </Stack>
    );
  }

  return (
    <Stack spacing="5">
      <ChangeNoticeSection event={event} />
      <AboutSection
        eventData={eventData}
        derivedPlaceOfPerformance={event.derivedPlaceOfPerformance}
        derivedCountySubdivision={event.derivedCountySubdivision}
        isPrivateEvent={!!privateContext}
        procedureType={publicContext?.procedureType}
      />
      {publicContext && <ClassificationSection publicContext={publicContext} />}
      {/* Award criteria and minimum requirements are optional outside the public flow, so both
          follow the data rather than the regime — a private procurement that has them shows them. */}
      {contest && <BuyerDesignContestSection contest={contest} />}
      {(publicContext || eventData.awardCriteria) && (
        <CriteriaSection
          eventData={eventData}
          showSelectionCriteria={!!publicContext}
          showAwardCriteria={!contest}
          awardCriteriaInOrderOfPriority={event.awardCriteriaInOrderOfPriority ?? false}
        />
      )}
      <MinimumRequirementsSection requirements={eventData.minimumRequirements ?? []} />
      {publicContext && <ContractSection eventData={eventData} publicContext={publicContext} />}
      <DocumentsSection eventId={event.id} documents={eventData.documents ?? []} />
      <ContractModificationsSection event={event} />
      {publicContext && <PublicationsSection publications={publicContext.publications ?? []} />}
      {privateContext && <InvitesSection event={{ ...event, eventContext: privateContext }} />}
      {event.status !== SourcingEventStatus.Draft && <ViewingsSection viewings={event.viewings ?? []} />}
    </Stack>
  );
};
